import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { feedService, interactionsService } from '@/api';
import { useToast } from '@/components/ui';
import { useAuth } from '@/providers';
import type { Content, FeedItem, UserContentState } from '@/types';

export function useContent(contentId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Busca o conteúdo do cache do feed
  const feedCache = queryClient.getQueryData<{ items: FeedItem[] }>(['feed', user?.id]);
  const cachedFeedItem = feedCache?.items.find((item) => item.content.id === contentId);

  // Subscreve ao cache de salvos para reagir a mudanças
  const { data: savedData } = useQuery<{ items: FeedItem[] }>({
    queryKey: ['saved', user?.id],
    queryFn: () => Promise.resolve({ items: [] }),
    enabled: false, // Não faz fetch, só usa o cache
  });
  const cachedSavedItem = savedData?.items.find((item) => item.content.id === contentId);

  // Usa o item do feed ou dos salvos
  const cachedItem = cachedFeedItem || cachedSavedItem;

  // Fetch from API if not in cache
  const {
    data: fetchedContent,
    isLoading: isFetchingContent,
    isError: isFetchError,
  } = useQuery<Content>({
    queryKey: ['content', contentId],
    queryFn: () => feedService.getContentFull(contentId),
    enabled: !!contentId && !cachedItem, // Only fetch if not in cache
    staleTime: 5 * 60 * 1000,
  });

  // Use cached content or fetched content
  const content = cachedItem?.content || fetchedContent;
  const isLoading = !cachedItem && isFetchingContent;
  const isError = !cachedItem && isFetchError;

  // Navigation: find current index and neighbors in feed
  const feedItems = feedCache?.items ?? [];
  const currentIndex = feedItems.findIndex((item) => item.content.id === contentId);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < feedItems.length - 1;
  const previousId = hasPrevious ? feedItems[currentIndex - 1].content.id : null;
  const nextId = hasNext ? feedItems[currentIndex + 1].content.id : null;

  const goToPrevious = () => {
    if (previousId) {
      navigate(`/content/${previousId}`, { replace: true });
    }
  };

  const goToNext = () => {
    if (nextId) {
      navigate(`/content/${nextId}`, { replace: true });
    }
  };

  // Track content view when loaded (for excluding seen content from feed)
  useEffect(() => {
    if (contentId && content) {
      // Fire and forget - don't block on this
      interactionsService.markRead(contentId).catch(() => {
        // Silently ignore errors - view tracking is not critical
      });
    }
  }, [contentId, content]);

  // Verifica se está salvo olhando o cache de salvos (fonte da verdade)
  const isSavedInCache = !!cachedSavedItem;

  // Estado local para rastrear interações (sincronizado com cache inicial)
  const [localUserState, setLocalUserState] = useState<UserContentState>(() => ({
    isLiked: cachedItem?.userState?.isLiked ?? false,
    isSaved: isSavedInCache || cachedItem?.userState?.isSaved || false,
    isDismissed: cachedItem?.userState?.isDismissed ?? false,
  }));

  // Sincroniza o estado local quando o cache de salvos muda
  useEffect(() => {
    setLocalUserState((prev) => ({
      ...prev,
      isSaved: isSavedInCache,
    }));
  }, [isSavedInCache]);

  // Update content state optimistically
  const updateContentState = (updates: Partial<UserContentState>) => {
    // Update local state first (for immediate UI feedback)
    setLocalUserState((prev) => ({ ...prev, ...updates }));

    // Also update in feed cache if present
    queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        items: old.items.map((item) =>
          item.content.id === contentId
            ? { ...item, userState: { ...item.userState, ...updates } }
            : item,
        ),
      };
    });

    // Also update saved cache
    queryClient.invalidateQueries({ queryKey: ['saved'] });
  };

  const likeMutation = useMutation({
    mutationFn: () => interactionsService.like(contentId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['content', contentId] });
      const previous = queryClient.getQueryData(['content', contentId]);
      updateContentState({ isLiked: true });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(['content', contentId], context?.previous);
    },
  });

  const unlikeMutation = useMutation({
    mutationFn: () => interactionsService.unlike(contentId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['content', contentId] });
      const previous = queryClient.getQueryData(['content', contentId]);
      updateContentState({ isLiked: false });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(['content', contentId], context?.previous);
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => interactionsService.save(contentId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['saved', user?.id] });
      const previousSaved = queryClient.getQueryData<{ items: FeedItem[] }>(['saved', user?.id]);

      // Update local state
      setLocalUserState((prev) => ({ ...prev, isSaved: true }));

      // Update feed cache
      queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.content.id === contentId
              ? { ...item, userState: { ...item.userState, isSaved: true } }
              : item,
          ),
        };
      });

      // Add to saved cache if we have the content
      if (cachedItem) {
        queryClient.setQueryData(['saved', user?.id], (old: { items: FeedItem[] } | undefined) => {
          const items = old?.items ?? [];
          // Don't add if already exists
          if (items.some((item) => item.content.id === contentId)) {
            return old;
          }
          return {
            items: [
              { ...cachedItem, userState: { ...cachedItem.userState, isSaved: true } },
              ...items,
            ],
          };
        });
      }

      return { previousSaved };
    },
    onError: (_err, _vars, context) => {
      setLocalUserState((prev) => ({ ...prev, isSaved: false }));
      if (context?.previousSaved) {
        queryClient.setQueryData(['saved', user?.id], context.previousSaved);
      }
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: () => interactionsService.unsave(contentId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['saved', user?.id] });
      const previousSaved = queryClient.getQueryData<{ items: FeedItem[] }>(['saved', user?.id]);

      // Update local state
      setLocalUserState((prev) => ({ ...prev, isSaved: false }));

      // Update feed cache
      queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.content.id === contentId
              ? { ...item, userState: { ...item.userState, isSaved: false } }
              : item,
          ),
        };
      });

      // Remove from saved cache
      queryClient.setQueryData(['saved', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.id !== contentId),
        };
      });

      return { previousSaved };
    },
    onError: (_err, _vars, context) => {
      setLocalUserState((prev) => ({ ...prev, isSaved: true }));
      if (context?.previousSaved) {
        queryClient.setQueryData(['saved', user?.id], context.previousSaved);
      }
    },
  });

  const dismissMutation = useMutation({
    mutationFn: () => interactionsService.dismiss(contentId),
    onSuccess: () => {
      // Remove from feed and navigate back
      queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.id !== contentId),
        };
      });
      navigate(-1);
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: () => interactionsService.feedback({ contentId, reason: 'not_relevant' }),
    onSuccess: () => {
      toast('Feedback registrado', 'success');
      // Remove from feed and navigate back
      queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.id !== contentId),
        };
      });
      navigate(-1);
    },
  });

  const toggleLike = () => {
    if (localUserState.isLiked) {
      unlikeMutation.mutate();
    } else {
      likeMutation.mutate();
    }
  };

  const toggleSave = () => {
    if (localUserState.isSaved) {
      unsaveMutation.mutate();
    } else {
      saveMutation.mutate();
    }
  };

  const getShareUrl = () => {
    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const cleanBase = baseUrl.replace(/\/$/, '');
    return `${cleanBase}/preview/${contentId}`;
  };

  const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(getShareUrl());
    openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`);
  };

  const shareToTwitter = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(content?.title || '');
    openShareWindow(`https://twitter.com/intent/tweet?text=${text}&url=${url}`);
  };

  const shareToWhatsApp = () => {
    const url = getShareUrl();
    const text = encodeURIComponent(`${content?.title || ''} - ${url}`);
    openShareWindow(`https://wa.me/?text=${text}`);
  };

  const copyShareLink = async () => {
    const url = getShareUrl();
    await navigator.clipboard.writeText(url);
    toast('Link copiado!', 'success');
  };

  const share = async () => {
    if (!content) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: content.title,
          text: content.summary,
          url: getShareUrl(),
        });
      } catch (err) {
        // User cancelled or share failed
        if ((err as Error).name !== 'AbortError') {
          await copyShareLink();
        }
      }
    } else {
      await copyShareLink();
    }
  };

  const muteAuthorMutation = useMutation({
    mutationFn: () => {
      if (!content?.author) throw new Error('No author to mute');
      return interactionsService.mute({ type: 'author', value: content.author });
    },
    onSuccess: () => {
      toast('Autor silenciado', 'success');
      // Remove all content from this author from feed
      queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.author !== content?.author),
        };
      });
      navigate(-1);
    },
  });

  const muteSourceMutation = useMutation({
    mutationFn: () => {
      if (!content?.source) throw new Error('No source to mute');
      return interactionsService.mute({ type: 'source', value: content.source });
    },
    onSuccess: () => {
      toast('Fonte silenciada', 'success');
      // Remove all content from this source from feed
      queryClient.setQueryData(['feed', user?.id], (old: { items: FeedItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.content.source !== content?.source),
        };
      });
      navigate(-1);
    },
  });

  return {
    content,
    userState: localUserState,
    isLoading,
    isError,
    error: null,
    toggleLike,
    toggleSave,
    dismiss: dismissMutation.mutate,
    feedback: feedbackMutation.mutate,
    muteAuthor: muteAuthorMutation.mutate,
    muteSource: muteSourceMutation.mutate,
    share,
    getShareUrl,
    shareToLinkedIn,
    shareToTwitter,
    shareToWhatsApp,
    copyShareLink,
    // Navigation
    hasPrevious,
    hasNext,
    goToPrevious,
    goToNext,
  };
}
